import styles from './loadShipmentItemsTable.module.css'
import { useEffect, useMemo, useState } from 'react'
import { BooleanField, Column, FieldType, FieldValue, Table } from '../../../../features/ui'
import { Product, ProductName, useProductLoaders } from '../../../products'
import { SaleOperation } from '../../../sales'
import { Delivery, useDeliveryHooks } from '../../../deliveries'

type Props = {
  operation: SaleOperation
  shipment: Delivery
  // Items already loaded on the truck: a reminder for the user, it never blocks the confirmation
  isLoaded: (itemId: string) => boolean
  onToggleLoaded: (itemId: string, loaded?: boolean) => void
}

export const LoadShipmentItemsTable = (props: Props) => {
  const { operation, shipment, isLoaded, onToggleLoaded } = props

  const [products, setProducts] = useState<Product[]>([])

  const { describeQuantity } = useDeliveryHooks()

  const productLoaders = useProductLoaders()

  useEffect(() => {
    const productIds = operation.items.map(item => item.productId)
    productLoaders().loadProducts(productIds).then(setProducts)
  }, [operation.items])

  const findProduct = (id: string) => products.find(product => product.id === id)

  const rows = useMemo(() => shipment.items
    .filter(shipmentItem => shipmentItem.quantity > 0)
    .map(shipmentItem => {
      const item = operation.items.find(item => item.id === shipmentItem.itemId)!
      return {
        productId: item.id,
        quantity: item.id
      }
    })
  , [])

  const columns: Column[] = [
    {
      name: 'productId',
      type: FieldType.TEXT,
      label: 'Producto',
      loading: products.length === 0,
      render: (value?: FieldValue) => {
        const itemId = value! as string
        const item = operation.items.find(item => item.id === itemId)!
        const product = findProduct(item.productId)!
        return (
          <div className={styles.productCell}>
            <BooleanField
              id={`loaded-${shipment.id}-${itemId}`}
              value={isLoaded(itemId)}
              optional
              onChange={loaded => onToggleLoaded(itemId, loaded)}
            />
            <div className={isLoaded(itemId) ? styles.loaded : ''}>
              <ProductName product={product} showSupplierCode />
            </div>
          </div>
        )
      }
    },
    {
      name: 'quantity',
      type: FieldType.NUMBER,
      label: 'Cantidad',
      loading: products.length === 0,
      render: (value?: FieldValue) => {
        const itemId = value! as string
        const deliveryItem = shipment.items.find(shipmentItem => shipmentItem.itemId === itemId)!
        const item = operation.items.find(item => item.id === itemId)!
        const product = findProduct(item.productId)
        return (
          <span className={isLoaded(itemId) ? styles.loaded : ''}>
            {describeQuantity(deliveryItem.quantity, product!)}
          </span>
        )
      }
    }
  ]

  return (
    <Table
      columns={columns}
      rows={rows}
      rowId='productId'
      readOnly
      small
      alignRight={columns.length === 2}
    />
  )
}
